import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import qs from 'qs';
import Header from './Header';
import { useAuth } from '../AuthContext';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${STRAPI_URL}${url}`;
};

// --- Inline renderer (text, bold, italic, etc.) ---
const renderInline = (node, i) => {
  if (node.type === 'link') {
    return (
      <a key={i} href={node.url} className="text-blue-600 underline" target="_blank" rel="noreferrer">
        {node.children.map(renderInline)}
      </a>
    );
  }
  let content = node.text || '';
  if (node.bold)          content = <strong>{content}</strong>;
  if (node.italic)        content = <em>{content}</em>;
  if (node.underline)     content = <u>{content}</u>;
  if (node.strikethrough) content = <s>{content}</s>;
  if (node.code)          content = <code className="bg-gray-100 rounded px-1 text-sm font-mono">{content}</code>;
  return <React.Fragment key={i}>{content}</React.Fragment>;
};

// --- Render children of a list-item: inline text plus nested lists ---
const renderListItemChildren = (children, keyPrefix) => {
  return children.map((child, i) => {
    if (child.type === 'list') {
      return renderList(child, `${keyPrefix}-nested-${i}`);
    }
    return renderInline(child, i);
  });
};

// --- List renderer: handles nested lists (both formats) ---
const renderList = (block, key) => {
  const Tag = block.format === 'ordered' ? 'ol' : 'ul';
  const className = block.format === 'ordered'
    ? 'list-decimal list-outside ml-5 my-1'
    : 'list-disc list-outside ml-5 my-1';

  const groups = [];
  let i = 0;
  while (i < block.children.length) {
    const child = block.children[i];
    if (child.type === 'list-item') {
      const siblingNested = [];
      let j = i + 1;
      while (j < block.children.length && block.children[j].type === 'list') {
        siblingNested.push(block.children[j]);
        j++;
      }
      groups.push({ item: child, siblingNested });
      i = j;
    } else if (child.type === 'list') {
      groups.push({ item: null, siblingNested: [child] });
      i++;
    } else {
      i++;
    }
  }

  return (
    <Tag key={key} className={className}>
      {groups.map((group, gi) => (
        <li key={gi} className="my-0.5">
          {group.item && renderListItemChildren(group.item.children, `${key}-${gi}`)}
          {group.siblingNested.map((nl, ni) => renderList(nl, `${key}-${gi}-sib-${ni}`))}
        </li>
      ))}
    </Tag>
  );
};

// --- Comment Box Renderer with Copy to Clipboard functionality ---
export const CommentCard = ({ block, renderInline }) => {
  const [copied, setCopied] = useState(false);

  const commentRegex = /^(💬\s*(Comment|Reporting\s+Comment):|💬\s+|\[COMMENT\]|Comment:)\s*/i;

  let prefixRemoved = false;
  let cleanedChildren = block.children.map((child) => {
    let text = child.text || '';
    // Convert literal '\n' sequences to real newlines
    text = text.replace(/\\n/g, '\n');

    if (text && !prefixRemoved) {
      const childMatch = text.match(commentRegex);
      if (childMatch) {
        prefixRemoved = true;
        text = text.substring(childMatch[0].length);
      }
    }
    return {
      ...child,
      text
    };
  });

  // Trim leading newlines and spaces from the first child text (after prefix removal)
  if (cleanedChildren.length > 0 && cleanedChildren[0].text) {
    cleanedChildren[0] = {
      ...cleanedChildren[0],
      text: cleanedChildren[0].text.replace(/^[\n\r\s]+/, '')
    };
  }

  // Helper to split children array at the first newline in any text node
  const splitChildrenByFirstNewline = (nodes) => {
    const heading = [];
    const body = [];
    let foundNewline = false;

    for (const node of nodes) {
      if (foundNewline) {
        body.push(node);
        continue;
      }

      if (node.text && node.text.includes('\n')) {
        const index = node.text.indexOf('\n');
        const before = node.text.substring(0, index);
        const after = node.text.substring(index + 1);

        if (before) {
          heading.push({ ...node, text: before });
        }
        if (after) {
          body.push({ ...node, text: after });
        }
        foundNewline = true;
      } else {
        heading.push(node);
      }
    }

    return { heading, body, hasNewline: foundNewline };
  };

  // Extract plain text for copying to clipboard (stripping rich formatting tags if any)
  const getPlainCopyToText = (nodes) => {
    return nodes.map(node => {
      if (node.type === 'link') {
        return getPlainCopyToText(node.children);
      }
      return node.text || '';
    }).join('');
  };

  const { heading, body, hasNewline } = splitChildrenByFirstNewline(cleanedChildren);

  let headingEl;
  let bodyEl;
  let textToCopy;

  if (hasNewline) {
    headingEl = heading.map(renderInline);
    // Trim leading whitespace/newlines from the first body element to avoid rendering gaps
    const cleanedBody = [...body];
    if (cleanedBody.length > 0 && cleanedBody[0].text) {
      cleanedBody[0] = {
        ...cleanedBody[0],
        text: cleanedBody[0].text.replace(/^\s+/, '')
      };
    }
    bodyEl = cleanedBody.map(renderInline);
    textToCopy = getPlainCopyToText(cleanedBody);
  } else {
    headingEl = 'Comment';
    bodyEl = cleanedChildren.map(renderInline);
    textToCopy = getPlainCopyToText(cleanedChildren);
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="comment-card group">
      <div className="comment-accent-bar" />
      <div className="comment-icon-container">
        <svg className="comment-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </div>
      <div className="comment-content">
        <div className="comment-header-row">
          <div className="comment-heading">{headingEl}</div>
          <button className="copy-button" onClick={handleCopy} title="Copy comment to report">
            {copied ? (
              <>
                <svg className="copy-btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg className="copy-btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                <span>Copy text</span>
              </>
            )}
          </button>
        </div>
        <div className="comment-text">
          {bodyEl}
        </div>
      </div>
    </div>
  );
};

// --- Top-level block renderer ---
const renderBlock = (block, i) => {
  switch (block.type) {
    case 'paragraph':
    case 'quote': {
      const fullText = block.children.map(child => child.text || '').join('').trim();
      const tipRegex = /^(💡\s*(Reporting\s+Tip|Tip):|💡\s+|\[TIP\]|Reporting\s+Tip:|Tip:)\s*/i;
      const isTip = tipRegex.test(fullText);

      if (isTip) {
        let prefixRemoved = false;
        const cleanedChildren = block.children.map((child) => {
          if (child.text && !prefixRemoved) {
            const childMatch = child.text.match(tipRegex);
            if (childMatch) {
              prefixRemoved = true;
              return {
                ...child,
                text: child.text.substring(childMatch[0].length)
              };
            }
          }
          return child;
        });

        return (
          <div key={i} className="practical-tip-card group">
            <div className="tip-accent-bar" />
            <div className="tip-icon-container">
              <svg className="tip-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="tip-content">
              <span className="tip-badge">Practical Reporting Tip</span>
              <div className="tip-text">{cleanedChildren.map(renderInline)}</div>
            </div>
          </div>
        );
      }

      const commentRegex = /^(💬\s*(Comment|Reporting\s+Comment):|💬\s+|\[COMMENT\]|Comment:)\s*/i;
      const isComment = commentRegex.test(fullText);

      if (isComment) {
        return <CommentCard key={i} block={block} renderInline={renderInline} />;
      }


      if (block.type === 'quote') {
        return (
          <blockquote key={i} className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-3">
            {block.children.map(renderInline)}
          </blockquote>
        );
      }

      return <p key={i} className="mb-2">{block.children.map(renderInline)}</p>;
    }

    case 'heading': {
      const sizes = { 1: 'text-3xl', 2: 'text-2xl', 3: 'text-xl', 4: 'text-lg', 5: 'text-base', 6: 'text-sm' };
      const Tag = `h${block.level}`;
      return <Tag key={i} className={`${sizes[block.level]} font-bold mt-4 mb-2`}>{block.children.map(renderInline)}</Tag>;
    }

    case 'list':
      return renderList(block, `block-${i}`);

    case 'code':
      return (
        <pre key={i} className="bg-gray-100 rounded p-3 overflow-x-auto my-3">
          <code className="text-sm font-mono">{block.children.map(renderInline)}</code>
        </pre>
      );

    case 'image':
      return (
        <img key={i} src={block.image?.url} alt={block.image?.alternativeText || ''} className="max-w-full rounded my-3" />
      );

    default:
      return null;
  }
};

// --- Main DiagnosisView Component (Strapi v5 flat structure) ---
const DiagnosisView = () => {
  const [searchParams] = useSearchParams();
  const diagnosisId = searchParams.get('diagnosisId');
  const token = searchParams.get('token');

  const [diagnosis, setDiagnosis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const { authFetch } = useAuth();

  // Keyboard navigation & body scroll-lock
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedEvidence(null);
      }
    };

    if (selectedEvidence) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedEvidence]);

  useEffect(() => {
    if (!diagnosisId) {
      setError("No diagnosis ID provided.");
      setIsLoading(false);
      return;
    }

    const query = qs.stringify({
      filters: { snomed_id: { $eq: diagnosisId.trim() } },
      populate: {
  chapter:      true,
  organ_system: true,
  evidence: { populate: ['file'] },
}
    }, { encodeValuesOnly: true });

    const apiUrl = `${STRAPI_URL}/api/diagnoses?${query}`;

    const fetchData = async () => {
      try {
        console.log('Fetching diagnosis with SNOMED ID:', diagnosisId);
        console.log('API URL:', apiUrl);
        const res = await authFetch(apiUrl);
        if (!res.ok) {
          const errorBody = await res.json();
          throw new Error(errorBody.error?.message || 'Failed to fetch diagnosis data.');
        }
        const data = await res.json();
        console.log('Diagnosis API response:', data);
        if (!data.data || data.data.length === 0)
          throw new Error(`Diagnosis with SNOMED ID '${diagnosisId}' not found.`);
        const diagnosisData = data.data[0];
        console.log('DIAGNOSIS CONTENT:', JSON.stringify(diagnosisData.content, null, 2));
        setDiagnosis(diagnosisData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [diagnosisId, token, authFetch]);

  if (isLoading) return <div className="text-center p-10">Loading evidence...</div>;
  if (error) return <div className="text-center p-10 text-red-600 font-bold">Error: {error}</div>;
  if (!diagnosis) return null;

  return (
    <div className="evidence-container">
      <Header />

      <main className="diagnosis-main">
        <div className="diagnosis-navigation">
          <Link to="/" className="back-button">← Back to Home</Link>
        </div>

        <div className="diagnosis-header">
          <h1 className="diagnosis-main-title">{diagnosis.title}</h1>
          <p className="diagnosis-breadcrumb">
            {diagnosis.organ_system?.data?.attributes.name} &gt; {diagnosis.chapter?.data?.attributes.display_title || diagnosis.chapter?.data?.attributes.title}
          </p>
        </div>

        <div className="diagnosis-content-grid">
          {/* Left Panel: Content */}
          <div className="diagnosis-text-section">
            <div className="diagnosis-text-content ck-content">
              {diagnosis.rich_content ? (
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: diagnosis.rich_content.replace(/src="\/uploads\//g, `src="${STRAPI_URL}/uploads/`) 
                  }} 
                />
              ) : Array.isArray(diagnosis.content) && diagnosis.content.length > 0 ? (
                <div>{diagnosis.content.map(renderBlock)}</div>
              ) : (
                <p className="text-gray-400 italic">No content available.</p>
              )}
            </div>
          </div>

          {/* Right Panel: Evidence */}
          <div className="diagnosis-evidence-section">
            <div className="evidence-section-inner">
              {diagnosis.evidence && diagnosis.evidence.length > 0 ? (
                <div className="evidence-items">
                  {diagnosis.evidence.map((item, index) => {
                    const isImage = item.file && item.file.mime?.startsWith('image/');
                    return (
                      <div
                        key={item.id || index}
                        className={`evidence-item-card ${isImage ? 'clickable-image-card' : ''}`}
                        onClick={isImage ? () => setSelectedEvidence(item) : undefined}
                      >
                        {item.file && (
                          item.file.mime?.startsWith('image/') ? (
                            <img
                              src={getFullUrl(item.file.url)}
                              alt={item.title || 'Evidence'}
                              className="evidence-image"
                            />
                          ) : (
                            <div className="evidence-file">
                              <a
                                href={getFullUrl(item.file.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="evidence-file-link"
                                onClick={(e) => e.stopPropagation()} // Prevent card click event
                              >
                                <svg className="file-icon" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-5L9 2H4z" clipRule="evenodd" />
                                </svg>
                                {item.file.name || 'Download PDF'}
                              </a>
                            </div>
                          )
                        )}
                        {(item.title || item.caption) && (
                          <div className="evidence-item-info">
                            {item.title && <h4 className="evidence-item-title">{item.title}</h4>}
                            {item.caption && <p className="evidence-item-caption">{item.caption}</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-evidence">No evidence items found.</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Attachment Modal Overlay */}
      {selectedEvidence && (
        <div className="evidence-modal-backdrop" onClick={() => setSelectedEvidence(null)}>
          <div className="evidence-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="evidence-modal-header">
              <span className="evidence-modal-title">Attachment</span>
              <button
                className="evidence-modal-close-x"
                onClick={() => setSelectedEvidence(null)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            <div className="evidence-modal-body">
              <div className="evidence-modal-image-wrapper">
                <img
                  src={getFullUrl(selectedEvidence.file.url)}
                  alt={selectedEvidence.title || 'Evidence Detail'}
                  className="evidence-modal-image"
                />
              </div>
              <div className="evidence-modal-view-original">
                <a
                  href={getFullUrl(selectedEvidence.file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="evidence-modal-link"
                >
                  View Original
                </a>
              </div>
              <div className="evidence-modal-text-content">
                {selectedEvidence.title && (
                  <h2 className="evidence-modal-item-title">{selectedEvidence.title}</h2>
                )}
                {selectedEvidence.caption && (
                  <p className="evidence-modal-item-caption">{selectedEvidence.caption}</p>
                )}
              </div>
            </div>
            <div className="evidence-modal-footer">
              <button className="evidence-modal-close-btn" onClick={() => setSelectedEvidence(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosisView;